import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { BsDownload } from 'react-icons/bs';
import { FaFilter } from 'react-icons/fa';
import { MdClear } from 'react-icons/md';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import AdminHeader from 'components/header';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import DateTimePicker from 'components/inputs/DateTimePicker';
import Select from 'components/inputs/Select';
import OffCanvas from 'components/inputs/OffCanvas';
import ReactPagination from 'components/general/ReactPagination';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { TOGGLE_MODULES } from 'components/common/constant';
import { useRoleHasModule } from 'utils/roleModuleToggles';
import { FETCH_CHARGING_STATION } from 'actions/chargingStation';
import { fetchSessionDataReport } from 'api/sessionDataReport';
import { reportDownloadRequest } from 'api/downloadAdvanceReport';
import { getTimezoneFromCountryCode } from 'utils/timezone/timezoneUtils';

// CDB-2017 — Session Data Report: the All Data Report without the
// columns the approved mapping sheet removes. Columns and rows come from the server,
// so the table always matches the Excel. Station and tenant scope is enforced there too.

const REPORT_KEY = TOGGLE_MODULES.SESSION_DATA_REPORT;
const REPORT_TITLE = 'Session Data Report';
const PAGE_SIZE = 20;
const ALLOWED_DAYS = 31;

const SessionDataReport = () => {
  const dispatch = useDispatch();
  const { navigateTo } = useContext(NavContext);

  const profileData = useSelector((state) => state.profile.userProfile);
  const allStations = useSelector((state) => state.chargingStation.chargingStations);
  const currentUserRole = _.get(profileData, 'role', '');
  const currentUserName = _.get(profileData, 'name', '');
  const assignedStationIds = _.get(profileData, 'charging_stations', []) || [];
  const countryTimezone = getTimezoneFromCountryCode(_.get(profileData, 'country_code', 'UTC'));

  const [startDate, setStartDate] = useState(moment().tz(countryTimezone).startOf('day'));
  const [endDate, setEndDate] = useState(moment(new Date()).tz(countryTimezone).endOf('day'));
  const [appliedFilter, setAppliedFilter] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [report, setReport] = useState({ columns: [], results: [], page: 1, totalPages: 0, totalResults: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloads, setDownloads] = useState([]);

  // Any role holding the report's module (Configure Role), and only those.
  const { roleLoaded, hasModule } = useRoleHasModule();
  const canView = hasModule(REPORT_KEY);

  // The page is not linked for anyone else; a direct link takes them back to their home page.
  useEffect(() => {
    if (currentUserRole && roleLoaded && !canView) {
      navigateTo('/dashboard');
    }
  }, [currentUserRole, roleLoaded, canView]);

  // Users with assigned stations pick from those; others from their tenant's stations.
  const stationOptions = (_.isEmpty(assignedStationIds) ? allStations : allStations.filter((station) => assignedStationIds.includes(station.id))).map(
    (station) => ({ label: station.name, value: station.id, chargers: station.chargers || [] })
  );

  const dateRange = useCallback(
    () => ({
      from: moment(startDate).tz(countryTimezone).utc(),
      to: moment(endDate).tz(countryTimezone).utc(),
    }),
    [startDate, endDate, countryTimezone]
  );

  const isRangeValid = () => {
    if (moment(endDate).diff(moment(startDate), 'days') > ALLOWED_DAYS) {
      toast.error(`The selected date range exceeds ${ALLOWED_DAYS} days. Please select a date range within ${ALLOWED_DAYS} days.`);
      return false;
    }
    return true;
  };

  const loadPage = useCallback(
    async (page = 1, filter = appliedFilter) => {
      setIsLoading(true);
      try {
        const res = await fetchSessionDataReport({ ...dateRange(), ...filter }, { page, limit: PAGE_SIZE });
        setReport({
          columns: _.get(res, 'data.columns', []),
          results: _.get(res, 'data.results', []),
          page: _.get(res, 'data.page', page),
          totalPages: _.get(res, 'data.totalPages', 0),
          totalResults: _.get(res, 'data.totalResults', 0),
        });
      } catch {
        setReport({ columns: [], results: [], page: 1, totalPages: 0, totalResults: 0 });
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilter, dateRange]
  );

  // The report's Report document (from the role's report list), needed to request and list files.
  const getReportDoc = useCallback(async () => {
    const listRes = await reportDownloadRequest.fetchReportList({ limit: 100 });
    return _.find(_.get(listRes, 'data.results', []), { key: REPORT_KEY });
  }, []);

  // Listed by report id, which a request carries from the moment it is created (its `type` is
  // only filled in once a background file is ready), so files still being prepared show too.
  const loadDownloads = useCallback(async () => {
    try {
      const reportDoc = await getReportDoc();
      if (!reportDoc) {
        setDownloads([]);
        return;
      }
      const res = await reportDownloadRequest.fetchReportDownloadRequest({ reportId: reportDoc.id, sortBy: '-createdAt', limit: 5 });
      setDownloads(_.get(res, 'data.results', []));
    } catch {
      setDownloads([]);
    }
  }, [getReportDoc]);

  useEffect(() => {
    if (!canView) return;
    dispatch({ type: FETCH_CHARGING_STATION.REQUEST, payload: { request_all: true } });
    loadPage(1);
    loadDownloads();
  }, [canView]);

  const handleSubmit = () => {
    if (isRangeValid()) loadPage(1);
  };

  const handleDownload = async () => {
    if (!isRangeValid() || isDownloading) return;
    setIsDownloading(true);
    try {
      const reportDoc = await getReportDoc();
      if (!reportDoc) {
        toast.error('This report is not available for your account.');
        return;
      }
      const range = dateRange();
      const prepareInBackground = reportDoc.max_days === 0 || moment(range.to).diff(moment(range.from), 'days') > reportDoc.max_days;
      const res = await reportDownloadRequest.createReportDownloadRequest({
        name: `${REPORT_TITLE} ${moment(startDate).format('DD-MM-YYYY')} to ${moment(endDate).format('DD-MM-YYYY')}`,
        type: reportDoc.id,
        filter: { ...range, ...appliedFilter },
      });
      if (_.get(res, 'status') !== 200) return;
      if (prepareInBackground) {
        toast.success('Your report is being prepared. It will appear under My Downloads when ready.');
        setTimeout(loadDownloads, 1000);
      } else {
        fileDownload(res.data, `${REPORT_TITLE}.xlsx`);
        toast.success('Report downloaded');
      }
    } catch {
      // The error body of a file request is a blob, so show a plain message.
      toast.error('The report could not be downloaded. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSavedDownload = async (id) => {
    const res = await reportDownloadRequest.getSignedUrl(id);
    const url = _.get(res, 'data.presignedUrl', '');
    if (url) {
      window.open(url, '_self');
    } else {
      toast.error('URL Not Found');
    }
  };

  const applyFilter = (values) => {
    const filter = {};
    if (!_.isEmpty(values.charging_station)) filter.charging_station = values.charging_station;
    if (!_.isEmpty(values.charger_id)) filter.charger_id = values.charger_id;
    setAppliedFilter(filter);
    setShowFilter(false);
    loadPage(1, filter);
  };

  if (!canView) {
    return null;
  }

  return (
    <>
      <LayoutContainer>
        <AdminHeader title={REPORT_TITLE} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="page-content-wrapper scrollable">
            <div className="data-report--main">
              <Card>
                <div className="data-report__inner">
                  <div className="data-report__box">
                    <Row className="data-report__row">
                      <Col xl={'auto'} md={'auto'}>
                        <div className="data-report-date_picker">
                          <DateTimePicker
                            allowedDays={ALLOWED_DAYS}
                            allowFlexibleDateRange={true}
                            onChangeOfStartDate={(item) => setStartDate(item)}
                            onChangeOfEndDate={(item) => setEndDate(item)}
                            initialValueOfStartDate={moment().startOf('day')}
                            initialValueOfEndDate={moment(new Date()).endOf('day')}
                          />
                        </div>
                      </Col>
                      <Col xl={'auto'} md={'auto'}>
                        <div className="report-search-box" onClick={handleSubmit}>
                          <Button className="report-search-button">Submit</Button>
                        </div>
                      </Col>
                      <Col xl={'auto'} md={'auto'}>
                        <div className="report-search-excel-block">
                          <div className="advance-filter-btn" onClick={() => setShowFilter(true)}>
                            <Button className="report-search-button">
                              <FaFilter className="hide-lap" />
                              <span>Advanced Filter</span>
                            </Button>
                          </div>
                        </div>
                      </Col>
                      <Col xl={'auto'} md={'auto'} className="excel-icon--block" onClick={handleDownload}>
                        {isDownloading ? (
                          <div className="loader--block">
                            <Spinner as="span" animation="border" size="sm" role="status" />
                          </div>
                        ) : (
                          <div className="report-excel-icon">
                            <BsDownload title="Download Excel" size={28} color={'#3c7cdd'} />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                  <div className="data-report__table">
                    <div className="table-responsive">
                      <table className="record-list-table" id="table-to-xls">
                        <thead>
                          <tr>
                            {report.columns.map((column) => (
                              <th key={column.header}>
                                <div className="sorting">
                                  <span>{column.header}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan={Math.max(report.columns.length, 1)}>
                                <Skeleton height={15} count={5} />
                              </td>
                            </tr>
                          ) : _.isEmpty(report.results) ? (
                            <tr>
                              <td colSpan={Math.max(report.columns.length, 1)} className="border-0">
                                <div className="empty-data-block">No data found</div>
                              </td>
                            </tr>
                          ) : (
                            report.results.map((row, rowIndex) => (
                              <tr key={`session-data-report-row-${report.page}-${rowIndex}`}>
                                {report.columns.map((column) => (
                                  <td key={column.header}>{_.isNil(row[column.key]) || row[column.key] === '' ? '-' : String(row[column.key])}</td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {!isLoading && !_.isEmpty(report.results) && (
                    <ReactPagination
                      currentPage={report.page}
                      limit={PAGE_SIZE}
                      total={report.totalResults}
                      handlePageClick={(pageVal) => loadPage(_.get(pageVal, 'selected', 0) + 1)}
                      totalPages={report.totalPages}
                      marginPagesDisplayed={1}
                    />
                  )}
                </div>
              </Card>
            </div>

            {!_.isEmpty(downloads) && (
              <div className="data-report--main">
                <Card>
                  <div className="data-report__inner">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">My Downloads</h5>
                      <Button className="report-search-button" onClick={loadDownloads}>
                        Refresh
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <table className="record-list-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Requested On</th>
                            <th>Status</th>
                            <th>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {downloads.map((item) => (
                            <tr key={item.id}>
                              <td>{item.name}</td>
                              <td>{moment(item.createdAt).tz(countryTimezone).format('DD/MM/YYYY HH:mm')}</td>
                              <td>{item.status || '-'}</td>
                              <td>
                                {item.s3url ? (
                                  <BsDownload
                                    title="Download"
                                    size={22}
                                    color={'#3c7cdd'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSavedDownload(item.id)}
                                  />
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </SkeletonTheme>
        {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
      </LayoutContainer>

      {showFilter && (
        <OffCanvas show={showFilter} onClose={() => setShowFilter(false)}>
          <Formik
            initialValues={{ charging_station: appliedFilter.charging_station || [], charger_id: appliedFilter.charger_id || [] }}
            onSubmit={(values, { setSubmitting }) => {
              applyFilter(values);
              setSubmitting(false);
            }}
          >
            {({ values, handleSubmit: submitForm, setFieldValue, resetForm }) => (
              <Form onSubmit={submitForm}>
                <div className="mb-5">
                  <div className="canvas-selection__block">
                    <Select
                      label="Station"
                      options={stationOptions.map(({ label, value }) => ({ label, value }))}
                      placeholder="Select Station"
                      name="charging_station"
                      isMulti
                      value={values.charging_station}
                      onMenuScrollDown={true}
                      onChange={(val) => {
                        setFieldValue('charging_station', _.isEmpty(val) ? [] : val);
                        setFieldValue('charger_id', []);
                      }}
                    />
                    <Select
                      label="Charger Id"
                      options={_.flatMap(
                        stationOptions.filter((station) => (values.charging_station || []).includes(station.value)),
                        (station) =>
                          station.chargers
                            .filter((charger) => !charger.is_deleted)
                            .map((charger) => ({ label: charger.charger_id, value: charger.id }))
                      )}
                      placeholder="Charger Id"
                      name="charger_id"
                      isMulti
                      value={values.charger_id}
                      onMenuScrollDown={true}
                      onChange={(val) => setFieldValue('charger_id', _.isEmpty(val) ? [] : val)}
                    />
                  </div>
                </div>
                <div className="canvas-btn__block">
                  <Button type="submit" className="apply--btn w-100">
                    Apply Filters
                  </Button>
                  <div
                    className="reset--block"
                    onClick={() => {
                      resetForm();
                      applyFilter({});
                    }}
                  >
                    <MdClear size={24} className="reset--icon" />
                    Clear
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </OffCanvas>
      )}
    </>
  );
};

export default SessionDataReport;
